import { Component, ChangeDetectionStrategy } from "@angular/core";
import { forkJoin } from "rxjs";
import { Router } from "@angular/router";
import { ActivatedRoute, Params } from "@angular/router";
import { DatePipe, NgIf, NgFor } from "@angular/common";
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { DomSanitizer } from "@angular/platform-browser";
import { DateTime } from "luxon";
import { AbstractSellerComponent } from "../abstract/seller.component";
import { MatDialog } from "@angular/material/dialog";
import { MatDatepicker, MatDatepickerInput } from "@angular/material/datepicker";
import { BillImage } from "../../obj/BillImage";
import { Pair } from "../../obj/Pair";
import { SellerContract } from "../../entity/SellerContract";
import { KilowantContract } from "../../entity/KilowantContract";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { ContractStatus } from "../../obj/ContractStatus";
import { User } from "../../entity/User";
import { State } from "../../entity/State";
import { ConfirmationDialog } from "../incs/confirmation-dialog.component";
import { DeclineDialog } from "../incs/decline-dialog.component";
import { SellerTermsViewDialogComponent } from "../incs/seller-terms-view-dialog.component";
import { MatTabChangeEvent, MatTabGroup, MatTab } from '@angular/material/tabs';
import { HeaderComponent } from "../incs/header.component";
import { NgxMaskDirective } from "ngx-mask";


@Component({
    selector: "kilowant-seller-verify-contract",
    templateUrl: "../../../html/seller/main-seller-verify-contract.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [HeaderComponent, NgIf, MatTabGroup, NgFor, MatTab, FormsModule, NgxMaskDirective, MatDatepickerInput, ReactiveFormsModule, MatDatepicker, DatePipe]
})
export class SellerVerifyContractComponent extends AbstractSellerComponent {
    user: User = new User();
    contract: KilowantContract = null!;
    states: State[] = [];
    termFrom = new UntypedFormControl();
    termTo = new UntypedFormControl();
    isViewOnly: boolean = false;
    title: string = "";
    sellerTermsAndConditions: SellerContract[] = [];
    selectedSellerTermsAndConditions: SellerContract = null!;
    billImages: BillImage[];
    showBuyerAddressLine2: boolean = false;
    showSellerAddressLine2: boolean = false;
    selectedBillImage: string = "";
    selectedBillAccountIndex: number = 1;
    accountNumberMask: string = "";
    accountNumbers: Pair[];
    accountNumber: string = "";

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        dialog: MatDialog,
        private sanitizer: DomSanitizer
    ) { super(router, route, dialog, authService, analyticsService, generalService); }

    ngOnInit(): void {
        this.generalService.getStates(false).then((states: any) => {
            this.states = states;
        });
        this.route.params.forEach((params: Params) => {
            let id: string = params["id"];
            this.generalService.getContract(Number(id))
                .then(contract => {
                    this.contract = contract;
                    if (this.contract.status != ContractStatus.PENDING_NON_VERIFIED) {
                        this.isViewOnly = true;
                    }
                    if ((this.contract.status == ContractStatus.OPEN || this.contract.status == ContractStatus.PENDING) && this.contract.canBeCancelled) {
                        this.isViewOnly = false;
                    }
                    if (this.contract.status == ContractStatus.PENDING_NON_VERIFIED) {
                        this.title = "Transaction Confirmation";
                    }
                    if (this.contract.status == ContractStatus.DECLINED_BY_SELLER) {
                        this.title = "Transaction Declined by Seller";
                    }
                    if (this.contract.status == ContractStatus.DECLINED_BY_BUYER) {
                        this.title = "Transaction Declined by Buyer";
                    }
                    if ((this.contract.status == ContractStatus.PENDING) ||
                        (this.contract.status == ContractStatus.OPEN) ||
                        (this.contract.status == ContractStatus.CLOSED)
                    ) {
                        this.title = "Active Price Plan";
                    }
                    if (this.contract.status == ContractStatus.DECLINED_VERIFICATION_TIMEOUT) {
                        this.title = "Price Plan Confirmation Timeout";
                    }
                    this.termFrom.setValue(DateTime.fromJSDate(this.contract.termFrom).toJSDate());
                    this.termTo.setValue(DateTime.fromJSDate(this.contract.termTo).toJSDate());
                    if (this.contract.detailsBuyerAddressLine2 != null && this.contract.detailsBuyerAddressLine2 != "") {
                        this.showBuyerAddressLine2 = true;
                    }
                    if (this.contract.detailsSellerAddressLine2 != null && this.contract.detailsSellerAddressLine2 != "") {
                        this.showSellerAddressLine2 = true;
                    }
                    this.billImages = [];
                    const billRefs: {id: number, accountIndex: number}[] = [];
                    for (let i = 0; i < this.contract.accounts.length; i++) {
                        for (let x = 0; x < this.contract.accounts[i].billImages.length; x++) {
                            billRefs.push({ id: this.contract.accounts[i].billImages[x].id, accountIndex: i });
                        }
                    }
                    if (billRefs.length > 0) {
                        // Bills are S3-only in this context. getDocumentUrl() is an authenticated
                        // call that returns a presigned URL — required because browsers cannot send
                        // Authorization headers on iframe src loads. No local-disk fallback: pre-S3
                        // bills are not present in seller contract views.
                        forkJoin(billRefs.map(b => this.generalService.getDocumentUrl(b.id))).subscribe(urls => {
                            this.billImages = urls.map((url, idx) => new BillImage(
                                idx + 1,
                                this.sanitizer.bypassSecurityTrustResourceUrl(url ?? ''),
                                billRefs[idx].accountIndex
                            ));
                            this.selectedBillImage = this.billImages[0]?.billImageUrl ?? '';
                        });
                    }

                    var accountList = this.contract.accounts;
                    this.accountNumbers = [];
                    for (var i = 0; i < accountList.length; i++) {
                        this.accountNumbers.push(new Pair(i, accountList[i].accountNumber));
                    }
                    this.accountNumberMask = (this.contract.utility.accountTemplate == null) ? "" : this.contract.utility.accountTemplate;
                    this.refreshView();
                });
        });

        this.generalService.getSellerContracts()
            .then(cs => {
                if (cs != null) {
                    this.sellerTermsAndConditions = cs;
                }
            });
    }

    getAccountNumberClasses(field: string, isRequired: boolean, value: string | null, index: number): string {
        var classes = this.getClassesForInput(field, isRequired, value);

        if (index == this.selectedBillAccountIndex) {
            if (classes.length > 0) {
                classes += " ";
            }
            classes += "selected-account-bill-image";
        }

        return classes;
    }

    updateAccountList(): void {
        var accountNumbersToUpdate = [];
        for (var i = 0; i < this.accountNumbers.length; i++) {
            accountNumbersToUpdate.push(this.accountNumbers[i].value);
        }
        this.contract.detailsAccountNumbers = accountNumbersToUpdate.join(',');
    }

    onTabChange(tabChangeEvent: MatTabChangeEvent): void {
        this.selectedBillImage = this.billImages[tabChangeEvent.index].billImageUrl;
        this.selectedBillAccountIndex = this.billImages[tabChangeEvent.index].accountIndex + 1;
    }

    confirmContract(): void {
        var dialogRef = this.dialog.open(ConfirmationDialog, {
            disableClose: false
            , panelClass: "card-popup"
        });
       dialogRef.componentInstance.confirmMessage = "Are you sure you want to confirm this price plan?"

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.generalService.confirmContract(this.contract, this.selectedSellerTermsAndConditions)
                    .subscribe((res: any) => {
                        this.router.navigate(["/seller/home/accepted-offers"]);
                    },
                        err => {
                            if (err.status == 400) {
                                alert(err.text);
                            }
                        });
            }
            dialogRef = null!;
        });
    }

    declineContract(): void {
        var dialogRef = this.dialog.open(DeclineDialog, {
            disableClose: false,
            panelClass: "card-popup"
        });
        dialogRef.componentInstance.declineMessage = "Please, provide reasons why this price plan is declined (optional)."

        dialogRef.afterClosed().subscribe((result: string | null) => {
            if (result != null) {
                this.generalService.declineContract(this.contract, result)
                    .subscribe({
                        next: () => {
                            this.router.navigate(["/seller/home/accepted-offers"]);
                            return;
                        },
                        error: (err: any) => {
                            if (err.status == 400) {
                                alert(err.text);
                            }
                            return;
                        }
                    });
            }
            dialogRef = null!;
        });
    }

    cancelPendingContract(): void {
        var dialogRef = this.dialog.open(DeclineDialog, {
            disableClose: false,
            panelClass: "card-popup"
        });
        dialogRef.componentInstance.declineTitle = "Cancel";
        dialogRef.componentInstance.declineMessage = "Please, provide reasons why this price plan is cancelled (optional)."
        dialogRef.componentInstance.declineButton = "Cancel";

        dialogRef.afterClosed().subscribe((result: string | null) => {
            if (result != null) {
                this.generalService.cancelPendingContractBySeller(this.contract, result)
                    .subscribe({
                        next: () => {
                            this.router.navigate(["/seller/home/accepted-offers"]);
                            return;
                        },
                        error: (err: any) => {
                            if (err.status == 400) {
                                alert(err.text);
                            }
                            return;
                        }
                    });
            }
            dialogRef = null!;
        });
    }

    downloadFile(id: number) {
        this.generalService.openDocument(id);
    }

    viewSellersTermsConditions(contract: KilowantContract): void {
        var viewDialogRef = this.dialog.open(SellerTermsViewDialogComponent, {
            disableClose: false
            , panelClass: "card-popup"
        });
        viewDialogRef.componentInstance.contract = contract;
        viewDialogRef.afterClosed().subscribe(result => {
            viewDialogRef = null!;
        });
    }

    isContractActive(status: ContractStatus): boolean {
        return status == ContractStatus.PENDING || status == ContractStatus.OPEN;
    }

    isContractToBeVerified(status: ContractStatus): boolean {
        return status == ContractStatus.PENDING_NON_VERIFIED;
    }

    isContractPending(status: ContractStatus): boolean {
        return status == ContractStatus.PENDING;
    }
}
